import { Delete, ZoomIn, ZoomOut } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { AppBar, Grid, Paper, Toolbar } from "@mui/material";
import { Button, Modal } from "antd";
import { Content } from "antd/es/layout/layout";
import dayjs from "dayjs";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { apiClient } from "../../service/api";
import budgetService from "../../service/budgetService";
import customerService from "../../service/serviceCustomer";
import userService from "../../service/serviceUser";
import NewAvailability from "../../view/availability/insert-availability";
import DataRangerPicker from "../datapicker/data-ranger-picker";
import DialogConfirm from "../dialog-confirm/dialog-confirm";
import ResponsiveDialog from "../dialog/dialog";
import SimpleBackdrop from "../loading/loading";
import MultipleSelect from "../multiple-select/multiple-select";
import Column from "./column";
import "./main.css";

const MainKanban = () => {
  /**
   * @function isMobile
   * @description Função para detectar se é um dispositivo móvel
   * @since 04/09/2025 - v2.7.29
   */
  const isMobile = () => {
    return window.innerWidth <= 768 || "ontouchstart" in window;
  };

  /**
   * @function checkHorizontalScroll
   * @description Função para verificar se o scroll horizontal está disponível
   * @since 04/09/2025 - v2.7.29
   */
  const checkHorizontalScroll = () => {
    const container = document.querySelector(".kanban-container");
    if (container) {
      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
      if (hasHorizontalScroll) {
        container.classList.add("scrollable");
      } else {
        container.classList.remove("scrollable");
      }
    }
  };

  /**
   * @function getCurrentMonthDates
   * @description Função para obter as datas padrão do mês atual
   * @returns {Object} - Objeto com as datas padrão do mês atual
   * @since 02/09/2025 - v2.7.27
   */
  const getCurrentMonthDates = () => {
    const now = dayjs();
    const startOfMonth = now.startOf("month");
    const endOfMonth = now.endOf("month");

    return {
      startDate: startOfMonth.format("YYYY-MM-DD"),
      endDate: endOfMonth.format("YYYY-MM-DD"),
      dateRange: [startOfMonth, endOfMonth],
    };
  };

  const defaultDates = getCurrentMonthDates();

  const [columns, setColumns] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState();
  const [messageDialog, setMessageDialog] = useState();
  const [titleDialog, setTitleDialog] = useState();
  const [statusDialog, setStatusDialog] = useState();
  const [openDialogYesOrNo, setOpenDialogYesOrNo] = useState();
  const [messageDialogYesOrNo, setMessageDialogYesOrNo] = useState();
  const [titleDialogYesOrNo, setTitleDialogYesOrNo] = useState();
  const [statusDialogYesOrNo, setStatusDialogYesOrNo] = useState();
  const [openDialogReloadYesOrNo, setOpenDialogReloadYesOrNo] = useState();
  const [messageDialogReloadYesOrNo, setMessageDialogReloadYesOrNo] = useState();
  const [titleDialogReloadYesOrNo, setTitleDialogReloadYesOrNo] = useState();
  const [statusDialogReloadYesOrNo, setStatusDialogReloadYesOrNo] = useState();
  const [loadingProgress, setLoadingProgress] = useState({
    current: 0,
    total: 0,
    message: "",
  });

  const [openDialogArchiveYesOrNo, setOpenDialogArchiveYesOrNo] = useState();
  const [messageDialogArchiveYesOrNo, setMessageDialogArchiveYesOrNo] = useState();
  const [titleDialogArchiveYesOrNo, setTitleDialogArchiveYesOrNo] = useState();
  const [statusDialogArchiveYesOrNo, setStatusDialogArchiveYesOrNo] = useState();

  const [zoomIn, setZoomIn] = useState(false);

  const [listCustomer, setListCustomer] = useState([]);
  const [listSaller, setListSaller] = useState([]);

  const [selectedCustomer, setSelectedCustomer] = useState([]);
  const [selectedSaller, setSelectedSaller] = useState([]);

  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);
  const [dateRange, setDateRange] = useState(defaultDates.dateRange);

  // Chave para localStorage dos filtros
  const FILTERS_STORAGE_KEY = 'kanban-filters';

  const [column, setColumn] = useState();
  const [task, setTask] = useState();

  // Flag para evitar múltiplas chamadas simultâneas
  const [isSearching, setIsSearching] = useState(false);

  /**
   * @function saveFiltersToStorage
   * @description Salva os filtros atuais no localStorage
   */
  const saveFiltersToStorage = () => {
    const filters = {
      selectedCustomer: selectedCustomer?.map(c => ({ idcustomer: c.idcustomer, name: c.name })) || [],
      selectedSaller: selectedSaller?.map(s => ({ iduser: s.iduser, name: s.name })) || [],
      startDate: startDate || '',
      endDate: endDate || '',
      dateRange: dateRange ? [dateRange[0]?.format('YYYY-MM-DD'), dateRange[1]?.format('YYYY-MM-DD')] : null
    };
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  };

  /**
   * @function loadFiltersFromStorage
   * @description Carrega os filtros salvos do localStorage
   */
  const loadFiltersFromStorage = () => {
    try {
      const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
      if (savedFilters) {
        const filters = JSON.parse(savedFilters);
        
        // Restaurar filtros apenas se não estiverem vazios
        if (filters.selectedCustomer && filters.selectedCustomer.length > 0) {
          setSelectedCustomer(filters.selectedCustomer);
        }
        if (filters.selectedSaller && filters.selectedSaller.length > 0) {
          setSelectedSaller(filters.selectedSaller);
        }
        if (filters.startDate) {
          setStartDate(filters.startDate);
        }
        if (filters.endDate) {
          setEndDate(filters.endDate);
        }
        if (filters.dateRange && filters.dateRange.length === 2) {
          const [start, end] = filters.dateRange;
          setDateRange([dayjs(start), dayjs(end)]);
        }
        
        return filters;
      }
    } catch (error) {
      console.error('Erro ao carregar filtros do localStorage:', error);
    }
    return null;
  };

  /**
   * @function calculateColumnTotal
   * @description Calcula o total de uma coluna baseado nos valores das tasks individuais
   * @param {Array} tasks - Array de tasks da coluna
   * @returns {number} - Total calculado
   */
  const calculateColumnTotal = (tasks) => {
    if (!tasks || tasks.length === 0) {
      return 0;
    }

    return tasks.reduce((total, task) => {
      const taskValue = parseFloat(task.ticket || 0);
      return total + taskValue;
    }, 0);
  };

  /**
   * @function makeRequestWithRetry
   * @description Faz uma requisição com retry automático em caso de falha
   * @param {Function} requestFn - Função que faz a requisição
   * @param {number} maxRetries - Número máximo de tentativas
   * @param {number} delay - Delay entre tentativas em ms
   * @returns {Promise} - Resposta da requisição
   */
  const makeRequestWithRetry = async (
    requestFn,
    maxRetries = 2,
    delay = 2000
  ) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Adicionar timeout personalizado para cada requisição
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Timeout da requisição')), 30000); // 30 segundos
        });

        const response = await Promise.race([requestFn(), timeoutPromise]);
        return response;
      } catch (error) {
        console.warn(`Tentativa ${attempt} falhou:`, error.message);
        if (attempt === maxRetries) {
          throw error;
        }
        // Aguarda antes da próxima tentativa (delay menor)
        await new Promise((resolve) =>
          setTimeout(resolve, delay * attempt)
        );
      }
    }
  };

  /**
   * @function processColumns
   * @description Processa as colunas recebidas
   * @param {Array} receivedColumns - Array de colunas recebidas
   * @returns {Array} - Array de colunas processadas
   */
  const processColumns = (receivedColumns) => {
    console.log("Processando colunas recebidas:", {
      receivedColumns: receivedColumns.length,
      columnDetails: receivedColumns.map((col) => ({
        name: col.name,
        idcolumn: col.idcolumn,
        taskCount: col.tasks ? col.tasks.length : 0,
        vltotalColumn: col.vltotalColumn,
      })),
    });

    // Processar cada coluna recebida
    const processedColumns = receivedColumns.map((column) => {
      const tasks = column.tasks || [];
      const backendTotal = parseFloat(column.vltotalColumn || 0);
      const calculatedTotal = calculateColumnTotal(tasks);

      // Usar o total calculado se for diferente do backend (correção de inconsistência)
      const finalTotal = calculatedTotal > 0 ? calculatedTotal : backendTotal;

      console.log(`Processando coluna ${column.name}:`, {
        idcolumn: column.idcolumn,
        backendTotal,
        calculatedTotal,
        finalTotal,
        taskCount: tasks.length,
      });

      return {
        ...column,
        tasks: tasks,
        vltotalColumn: finalTotal,
      };
    });

    // Ordena as tasks de cada coluna por data de criação (mais recente primeiro)
    processedColumns.forEach((column) => {
      if (column.tasks && column.tasks.length > 0) {
        column.tasks.sort((a, b) => {
          const dateA = new Date(a.dtcreated || 0);
          const dateB = new Date(b.dtcreated || 0);
          return dateB - dateA; // Ordem decrescente (mais recente primeiro)
        });
      }
    });

    // Ordenar colunas por idcolumn
    processedColumns.sort((a, b) => a.idcolumn - b.idcolumn);

    console.log("Colunas processadas:", processedColumns.map(col => ({
      name: col.name,
      idcolumn: col.idcolumn,
      taskCount: col.tasks ? col.tasks.length : 0,
      vltotalColumn: col.vltotalColumn
    })));

    return processedColumns;
  };


  /**
   * @function searchBudgets
   * @description Busca orçamentos com base nos filtros fornecidos
   * @param {Object} customParams - Parâmetros customizados para sobrescrever os padrões
   * @since 28/08/2025 - v2.7.27
   */
  const searchBudgets = async (customParams = {}) => {
    // Evitar múltiplas chamadas simultâneas
    if (isSearching) {
      console.log("Busca já em andamento, ignorando chamada duplicada");
      return;
    }

    // Construir parâmetros de busca considerando valores explícitos null
    const customerIds = customParams.hasOwnProperty("customerIds")
      ? customParams.customerIds
      : selectedCustomer?.map((c) => c.idcustomer) || [];

    const sallerIds = customParams.hasOwnProperty("sallerIds")
      ? customParams.sallerIds
      : selectedSaller?.map((s) => s.iduser) || [];

    const searchStartDate = customParams.hasOwnProperty("startDate")
      ? customParams.startDate
      : startDate || null;

    const searchEndDate = customParams.hasOwnProperty("endDate")
      ? customParams.endDate
      : endDate || null;

    try {
      setIsSearching(true);
      setIsLoading(true);

      const params = new URLSearchParams();

      console.log("Verificando condições de busca:", {
        hasCustomers: customerIds && customerIds.length > 0,
        hasSallers: sallerIds && sallerIds.length > 0,
        customerIds,
        sallerIds,
      });

      // Busca otimizada com múltiplos IDs
      if (customerIds && customerIds.length > 0) {
        console.log("Buscando para clientes:", customerIds);
        console.log("Vendedores selecionados:", sallerIds);

        // Atualiza progresso inicial
        setLoadingProgress({
          current: 0,
          total: 1,
          message: `Carregando informações...`,
        });

        // Construir parâmetros para busca com múltiplos IDs
        const searchParams = new URLSearchParams();

        // Adicionar múltiplos IDs como string separada por vírgula
        if (customerIds.length > 0) {
          searchParams.append("customerIds", customerIds.join(","));
        }

        if (sallerIds.length > 0) {
          searchParams.append("sallerIds", sallerIds.join(","));
        }

        if (searchStartDate) {
          searchParams.append("startDate", searchStartDate);
        }

        if (searchEndDate) {
          searchParams.append("endDate", searchEndDate);
        }

        console.log("Parâmetros da busca otimizada:", searchParams.toString());

        // Fazer uma única requisição com todos os IDs
        const response = await makeRequestWithRetry(() =>
          budgetService.searchBudget(searchParams.toString())
        );

        const allBudgets = response.data || [];

        console.log("Dados recebidos da API:", allBudgets);
        console.log("Quantidade de colunas encontradas:", allBudgets.length);

        // Se os dados já vêm no formato de colunas, usar diretamente
        if (allBudgets.length > 0 && allBudgets[0].hasOwnProperty('idcolumn')) {
          console.log("Dados já estão no formato de colunas, processando diretamente");
          const finalColumns = processColumns(allBudgets);
          setColumns(finalColumns);
          return;
        }

        // Caso contrário, fazer o agrupamento (lógica antiga)
        console.log("Fazendo agrupamento de budgets...");
        console.log(
          "Total de tasks em todos os orçamentos:",
          allBudgets.reduce(
            (total, budget) => total + (budget.tasks ? budget.tasks.length : 0),
            0
          )
        );

        const columnMap = new Map();

        allBudgets.forEach((budget) => {
          const columnName = budget.name;
          const columnId = budget.idcolumn;

          // Incluir todas as colunas, mesmo as vazias
          if (!columnMap.has(columnName)) {
            columnMap.set(columnName, {
              idcolumn: columnId,
              name: columnName,
              tasks: [...(budget.tasks || [])],
              iduser: budget.iduser,
              vltotalColumn: 0, // Será calculado depois baseado nas tasks
            });
          } else {
            // Se a coluna existe, adiciona tarefas à coluna existente
            const existingColumn = columnMap.get(columnName);
            existingColumn.tasks = [
              ...existingColumn.tasks,
              ...(budget.tasks || []),
            ];
          }
        });

        // Calcular o total de cada coluna baseado nas tasks individuais
        columnMap.forEach((column) => {
          column.vltotalColumn = calculateColumnTotal(column.tasks);
        });

        // Converte o mapa para array e ordena por idcolumn
        const groupedColumns = Array.from(columnMap.values()).sort(
          (a, b) => a.idcolumn - b.idcolumn
        );

        // Ordena as tasks de cada coluna por data de criação (mais recente primeiro)
        groupedColumns.forEach((column) => {
          if (column.tasks && column.tasks.length > 0) {
            column.tasks.sort((a, b) => {
              const dateA = new Date(a.dtcreated || 0);
              const dateB = new Date(b.dtcreated || 0);
              return dateB - dateA; // Ordem decrescente (mais recente primeiro)
            });
          }
        });

        console.log("Colunas agrupadas:", groupedColumns);
        console.log(
          "Total de tasks por coluna:",
          groupedColumns.map((col) => ({
            name: col.name,
            taskCount: col.tasks.length,
          }))
        );
        console.log(
          "Total de tasks em todas as colunas:",
          groupedColumns.reduce((total, col) => total + col.tasks.length, 0)
        );

        // Processar as colunas agrupadas
        const finalColumns = processColumns(groupedColumns);
        console.log("Colunas finais processadas:", finalColumns);
        console.log(
          "Total de tasks nas colunas finais:",
          finalColumns.reduce(
            (total, col) => total + (col.tasks ? col.tasks.length : 0),
            0
          )
        );
        setColumns(finalColumns);
        return;
      }

      // Busca otimizada apenas para vendedores
      if (sallerIds && sallerIds.length > 0) {
        console.log("Buscando apenas por vendedores:", sallerIds);

        setLoadingProgress({
          current: 0,
          total: 1,
          message: `Carregando informações...`,
        });

        // Construir parâmetros para busca com múltiplos IDs de vendedores
        const searchParams = new URLSearchParams();

        // Adicionar múltiplos IDs de vendedores como string separada por vírgula
        searchParams.append("sallerIds", sallerIds.join(","));

        if (searchStartDate) {
          searchParams.append("startDate", searchStartDate);
        }

        if (searchEndDate) {
          searchParams.append("endDate", searchEndDate);
        }

        console.log("Parâmetros da busca otimizada (vendedores):", searchParams.toString());

        // Fazer uma única requisição com todos os IDs de vendedores
        const response = await makeRequestWithRetry(() =>
          budgetService.searchBudget(searchParams.toString())
        );

        console.log("Resposta da busca otimizada (vendedores):", response);

        const allBudgets = response.data || [];

        console.log("Dados recebidos da API (vendedores):", allBudgets);
        console.log("Quantidade de colunas encontradas:", allBudgets.length);

        // Se os dados já vêm no formato de colunas, usar diretamente
        if (allBudgets.length > 0 && allBudgets[0].hasOwnProperty('idcolumn')) {
          console.log("Dados já estão no formato de colunas, processando diretamente");
          const finalColumns = processColumns(allBudgets);
          setColumns(finalColumns);
          return;
        }

        const columnMap = new Map();

        allBudgets.forEach((budget) => {
          const columnName = budget.name;
          const columnId = budget.idcolumn;

          // Incluir todas as colunas, mesmo as vazias
          if (!columnMap.has(columnName)) {
            columnMap.set(columnName, {
              idcolumn: columnId,
              name: columnName,
              tasks: [...(budget.tasks || [])],
              iduser: budget.iduser,
              vltotalColumn: 0, // Será calculado depois baseado nas tasks
            });
          } else {
            // Se a coluna existe, adiciona tarefas à coluna existente
            const existingColumn = columnMap.get(columnName);
            existingColumn.tasks = [
              ...existingColumn.tasks,
              ...(budget.tasks || []),
            ];
          }
        });

        // Calcular o total de cada coluna baseado nas tasks individuais
        columnMap.forEach((column) => {
          column.vltotalColumn = calculateColumnTotal(column.tasks);
        });

        // Converte o mapa para array e ordena por idcolumn
        const groupedColumns = Array.from(columnMap.values()).sort(
          (a, b) => a.idcolumn - b.idcolumn
        );

        // Ordena as tasks de cada coluna por data de criação (mais recente primeiro)
        groupedColumns.forEach((column) => {
          if (column.tasks && column.tasks.length > 0) {
            column.tasks.sort((a, b) => {
              const dateA = new Date(a.dtcreated || 0);
              const dateB = new Date(b.dtcreated || 0);
              return dateB - dateA; // Ordem decrescente (mais recente primeiro)
            });
          }
        });

        console.log("Colunas agrupadas (vendedores):", groupedColumns);

        // processar as colunas agrupadas
        const finalColumns = processColumns(groupedColumns);
        setColumns(finalColumns);
        return;
      }

      if (searchStartDate) {
        params.append("startDate", searchStartDate);
      }

      if (searchEndDate) {
        params.append("endDate", searchEndDate);
      }

      const queryString = params.toString();

      console.log("Executando busca normal - Query string:", queryString);
      console.log("Parâmetros da busca normal:", {
        customerIds,
        sallerIds,
        searchStartDate,
        searchEndDate,
      });
      const response = await budgetService.searchBudget(queryString);
      console.log("Resposta da busca normal:", response);

      if (response.success && response.data) {
        console.log("Dados da busca normal:", response.data);
        const finalColumns = processColumns(response.data);
        setColumns(finalColumns);
      } else {
        console.log("Dados da busca normal (fallback):", response.data || []);
        const finalColumns = processColumns(response.data || []);
        setColumns(finalColumns);
      }
    } catch (error) {
      console.error("Erro ao buscar orçamentos:", error);
      // Fallback para endpoint antigo em caso de erro
      try {
        const fallbackResponse = await budgetService.listBudget();
        const finalColumns = processColumns(
          fallbackResponse.data || []
        );
        setColumns(finalColumns);
      } catch (fallbackError) {
        console.error("Erro no fallback:", fallbackError);
        // Em caso de erro total, não mostrar nenhuma coluna
        setColumns([]);
      }
    } finally {
      setIsLoading(false);
      setIsSearching(false);
      setLoadingProgress({ current: 0, total: 0, message: "" });
    }
  };

  /**
   * @function fetchData
   * @description Busca os dados do backend
   */
  const fetchData = async () => {
    try {
      setIsLoading(true);
      // Buscar todos os orçamentos do mês atual por padrão
      await searchBudgets({
        customerIds: [],
        sallerIds: [],
        startDate: defaultDates.startDate,
        endDate: defaultDates.endDate,
      });
    } catch (error) {
      console.error("Erro ao buscar dados do backend:", error);
      // Em caso de erro, não mostrar nenhuma coluna
      setColumns([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Verificar scroll horizontal quando as colunas mudam
  useEffect(() => {
    if (columns.length > 0) {
      // Aguardar um pouco para o DOM ser renderizado
      setTimeout(() => {
        checkHorizontalScroll();
      }, 100);
    }
  }, [columns]);

  // Verifica o scroll horizontal quando a janela é redimensionada
  useEffect(() => {
    const handleResize = () => {
      checkHorizontalScroll();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // Carregar filtros salvos primeiro
    const savedFilters = loadFiltersFromStorage();
    
    // Se há filtros salvos, usar eles para buscar dados
    if (savedFilters) {
      // Aguardar um pouco para os estados serem atualizados
      setTimeout(async () => {
        await searchBudgets({
          customerIds: savedFilters.selectedCustomer?.map(c => c.idcustomer) || [],
          sallerIds: savedFilters.selectedSaller?.map(s => s.iduser) || [],
          startDate: savedFilters.startDate || null,
          endDate: savedFilters.endDate || null,
        });
      }, 100);
    } else {
      // Se não há filtros salvos, usar dados padrão
      fetchData();
    }

    var listCust = [];
    customerService.listCustomerActive(1, 1000).then(item => {
      item.data.forEach(element => {
        listCust.push(element);
      });
      setListCustomer(listCust);
    }).catch(error => {
      console.log(error);
    });

    var listSaller = [];
    userService.listarUser(1, 1000).then(item => {
      item.data.forEach(element => {
        listSaller.push(element);
      });
      setListSaller(listSaller);
    }).catch(error => {
      console.log(error);
    });
  }, []);

  /**
   * @function moveTask
   * @description Move uma tarefa de uma coluna para outra na posição especificada
   * @param {string} taskId - ID da tarefa
   * @param {string} sourceColumnId - ID da coluna de origem
   * @param {string} targetColumnId - ID da coluna de destino
   * @param {number} targetIndex - Índice de destino na coluna (posição exata onde soltar)
   */
  const moveTask = async (taskId, sourceColumnId, targetColumnId, targetIndex = -1) => {
    console.log('🎯 [moveTask] Movendo card:', { taskId, sourceColumnId, targetColumnId, targetIndex });
    
    setColumns((prev) => {
      const source = prev.find((c) => c.idcolumn === sourceColumnId);
      const target = prev.find((c) => c.idcolumn === targetColumnId);
      
      if (!source || !target) {
        console.warn('❌ [moveTask] Coluna não encontrada');
        return prev;
      }
      
      const taskToMove = source.tasks.find((t) => t.idbudget === taskId);
      
      if (!taskToMove) {
        console.warn('❌ [moveTask] Task não encontrada');
        return prev;
      }
      
      // Adicionar task na coluna de destino na posição especificada
      let updatedTargetTasks;
      let updatedSourceTasks;
      
      if (sourceColumnId === targetColumnId) {
        // Mover dentro da mesma coluna (reordenar)
        console.log('🔄 [moveTask] Reordenando dentro da mesma coluna');
        
        // Encontrar o índice atual do card
        const currentIndex = source.tasks.findIndex((t) => t.idbudget === taskId);
        console.log(`📍 [moveTask] Índice atual: ${currentIndex}, Novo índice: ${targetIndex}`);
        
        updatedTargetTasks = [...source.tasks];
        
        // Remover o card da posição atual
        const [movedTask] = updatedTargetTasks.splice(currentIndex, 1);
        
        // Calcular o novo índice (ajustar se estiver movendo para baixo na mesma coluna)
        let adjustedIndex = targetIndex;
        if (targetIndex > currentIndex) {
          // Se mover para baixo, precisa ajustar o índice porque já removemos o item
          adjustedIndex = targetIndex - 1;
        }
        
        // Inserir na nova posição
        updatedTargetTasks.splice(adjustedIndex, 0, movedTask);
        
        console.log(`✅ [moveTask] Card reordenado de ${currentIndex} para ${adjustedIndex}`);
      } else {
        // Mover para coluna diferente
        console.log('↔️ [moveTask] Movendo para coluna diferente');
        
        // Remover task da coluna de origem
        updatedSourceTasks = source.tasks.filter((t) => t.idbudget !== taskId);
        
        if (targetIndex >= 0 && targetIndex <= target.tasks.length) {
          // Inserir na posição específica
          updatedTargetTasks = [...target.tasks];
          updatedTargetTasks.splice(targetIndex, 0, taskToMove);
          console.log(`✅ [moveTask] Card inserido na posição ${targetIndex}`);
        } else {
          // Adicionar no final se não houver índice válido
          updatedTargetTasks = [...target.tasks, taskToMove];
          console.log('✅ [moveTask] Card adicionado no final da coluna');
        }
      }
      
      return prev.map((c) =>
        c.idcolumn === sourceColumnId
          ? { ...c, tasks: sourceColumnId === targetColumnId ? updatedTargetTasks : updatedSourceTasks, vltotalColumn: calculateColumnTotal(sourceColumnId === targetColumnId ? updatedTargetTasks : updatedSourceTasks) }
          : c.idcolumn === targetColumnId && sourceColumnId !== targetColumnId
            ? { ...c, tasks: updatedTargetTasks, vltotalColumn: calculateColumnTotal(updatedTargetTasks) }
            : c
      );
    });
    
    // Só salvar no backend se o card mudou de coluna
    if (sourceColumnId !== targetColumnId) {
      try {
        await apiClient.put('/update-task-position', {
          taskId,
          targetColumnId,
        });
        console.log('✅ [moveTask] Mudança de coluna salva no backend');
      } catch (error) {
        console.error('❌ [moveTask] Erro ao salvar no backend:', error);
        await fetchData();
      }
    } else {
      console.log('ℹ️ [moveTask] Reordenação dentro da mesma coluna - não salvo no backend');
    }
  };

  const deleteTask = async (taskId, columnId) => {
    try {
      const response = await apiClient.delete(`/budget/${taskId}`);
      if (response.status === 204 || response.success) {
        setColumns((prev) =>
          prev.map((c) =>
            c.idcolumn === columnId
              ? { ...c, tasks: c.tasks.filter((t) => t.idbudget !== taskId), vltotalColumn: calculateColumnTotal(c.tasks) }
              : c
          )
        );
        setOpenDialog(true);
        setStatusDialog("success");
        setTitleDialog("Muito bem!");
        setMessageDialog("Orçamento excluido com sucesso");
      }
    } catch { }
  };

  const reloadTask = async () => {
    await fetchData();
  };

  const openHandleOk = () => setIsModalVisible(true);
  const handleOk = () => setIsModalVisible(false);
  const handleCancel = () => setIsModalVisible(false);
  const handleClose = () => setIsLoading(false);
  const handleDialogClose = () => setOpenDialog(false);
  const deleteTaskIntern = (taskId, columnId) => {
    setTask(taskId);
    setColumn(columnId);
    setStatusDialogYesOrNo("error");
    setTitleDialogYesOrNo("Excluir Orçamento ");
    setMessageDialogYesOrNo("Tem certeza que deseja excluir este orçamento ? ");
    setOpenDialogYesOrNo(true);
  };
  const fetchUpdatedColumns = async () => {
    try {
      // Salvar filtros antes de buscar dados atualizados
      saveFiltersToStorage();
      
      // Buscar dados com os filtros atuais aplicados
      await searchBudgets({
        customerIds: selectedCustomer?.map((c) => c.idcustomer) || [],
        sallerIds: selectedSaller?.map((s) => s.iduser) || [],
        startDate: startDate || null,
        endDate: endDate || null,
      });
      
      setIsModalVisible(false);
    } catch (error) {
      console.error("Erro ao buscar dados atualizados do backend:", error);
    }
  };
  const handleSaveOk = () => fetchUpdatedColumns();
  const onReloadIntern = () => fetchUpdatedColumns();
  const handleDialogCloseYesOrNo = () => setOpenDialogYesOrNo(false);
  const handleDialogReloadCloseYesOrNo = () => setOpenDialogReloadYesOrNo(false);
  const deleteCommissionAndRecalc = () => {
    deleteTask(task, column);
    setOpenDialogYesOrNo(false);
  };
  const deleteCommissionReloadAndRecalc = () => {
    reloadTask(task, column);
    setOpenDialogReloadYesOrNo(false);
  };

  const handleChangeCustomer = (newValue) => {
    setSelectedCustomer(newValue);
    // Salvar filtros automaticamente quando mudarem
    setTimeout(() => saveFiltersToStorage(), 100);
  };

  const handleChangeSaller = (newValue) => {
    setSelectedSaller(newValue);
    // Salvar filtros automaticamente quando mudarem
    setTimeout(() => saveFiltersToStorage(), 100);
  };

  /**
   * @function handleDateRangeChange
   * @description Função para lidar com a mudança do range de datas
   * @param {Array} dates - Array com as datas selecionadas [startDate, endDate]
   * @since 28/08/2025 - v2.7.27
   */
  const handleDateRangeChange = async (dates) => {
    setDateRange(dates);

    let newStartDate = "";
    let newEndDate = "";

    if (!dates || dates.length === 0) {
      setStartDate("");
      setEndDate("");
    } else {
      newStartDate = dates[0].format("YYYY-MM-DD");
      newEndDate = dates[1].format("YYYY-MM-DD");

      setStartDate(newStartDate);
      setEndDate(newEndDate);
    }

    // Salvar filtros automaticamente quando mudarem
    setTimeout(() => saveFiltersToStorage(), 100);

    await searchBudgets({
      customerIds: selectedCustomer?.map((c) => c.idcustomer) || [],
      sallerIds: selectedSaller?.map((s) => s.iduser) || [],
      startDate: newStartDate || null,
      endDate: newEndDate || null,
    });
  };

  /**
   * @function applyFilters
   * @description Função para aplicar todos os filtros selecionados
   * @since 29/08/2025 - v2.7.27
   */
  const applyFilters = async () => {
    try {
      setIsLoading(true);

      // Fazer a busca com os filtros aplicados
      await searchBudgets({
        customerIds: selectedCustomer?.map((c) => c.idcustomer) || [],
        sallerIds: selectedSaller?.map((s) => s.iduser) || [],
        startDate: startDate || null,
        endDate: endDate || null,
      });
    } catch (error) {
      console.error("Erro ao aplicar filtros:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * @function clearFilters
   * @description Função para limpar todos os filtros e buscar todos os orçamentos
   */
  const clearFilters = async () => {
    try {
      setIsLoading(true);

      // Limpar todos os filtros
      setSelectedCustomer([]);
      setSelectedSaller([]);
      setStartDate("");
      setEndDate("");
      setDateRange(null);

      // Limpar filtros do localStorage também
      localStorage.removeItem(FILTERS_STORAGE_KEY);

      // Buscar todos os dados sem nenhum filtro
      await searchBudgets({
        customerIds: [],
        sallerIds: [],
        startDate: null,
        endDate: null,
      });
    } catch (error) {
      console.error("Erro ao limpar filtros:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * @function applyZoom
   * @description Função para aplicar zoom na tela
   * @param {*} type - Tipo de zoom (in ou out)
   * @since 04/09/2025 - v2.7.29
   */
  const applyZoom = (type) => {
    const kanbanContainer = document.querySelector(".kanban-container");
    if (!kanbanContainer) return;

    const currentZoom = parseFloat(kanbanContainer.style.zoom) || 1;
    const zoomStep = 0.1; // Incremento/decremento de zoom
    const minZoom = 0.7; // Limite mínimo (3x menor)
    const maxZoom = 1.3; // Limite máximo (3x maior)

    let newZoom;
    if (type === "in") {
      newZoom = Math.min(currentZoom + zoomStep, maxZoom);
      setZoomIn(newZoom > 1);
    } else {
      newZoom = Math.max(currentZoom - zoomStep, minZoom);
      setZoomIn(newZoom > 1);
    }

    kanbanContainer.style.zoom = newZoom.toString();

    console.log(
      `Zoom aplicado: ${newZoom}x (${type === "in" ? "aumentar" : "diminuir"})`
    );
  };

  return (
    <DndProvider
      backend={isMobile() ? TouchBackend : HTML5Backend}
      options={
        isMobile()
          ? {
            enableMouseEvents: false,
            enableKeyboardEvents: false,
            enableTouchEvents: true,
            delay: 0,
            delayTouchStart: 0,
            touchSlop: 5,
          }
          : {
            enableMouseEvents: true,
            enableKeyboardEvents: true,
            enableTouchEvents: true,
          }
      }
      style={{
        position: "relative",
        zIndex: 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {isLoading ? (
        <SimpleBackdrop
          open={true}
          handleClose={handleClose}
          title={loadingProgress.message || "Carregando informações..."}
        />
      ) : (
        <Content
          style={{
            position: "relative",
            zIndex: 1,
            overflow: "visible",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "none",
          }}
        >
          <ResponsiveDialog open={openDialog} handleClose={handleDialogClose} title={titleDialog} message={messageDialog} status={statusDialog} />
          <DialogConfirm open={openDialogYesOrNo} handleClose={handleDialogCloseYesOrNo} handleOk={deleteCommissionAndRecalc} title={titleDialogYesOrNo} message={messageDialogYesOrNo} status={statusDialogYesOrNo} />
          <DialogConfirm open={openDialogReloadYesOrNo} handleClose={handleDialogReloadCloseYesOrNo} handleOk={deleteCommissionReloadAndRecalc} title={titleDialogReloadYesOrNo} message={messageDialogReloadYesOrNo} status={statusDialogReloadYesOrNo} />
          <Paper sx={{ width: "100%", maxWidth: "100%", height: "calc(100vh - 160px)", margin: 0, overflowY: "visible", display: "flex", flexDirection: "column", position: "relative", zIndex: 1, boxShadow: 3, borderRadius: "8px" }}>
            <AppBar
              position="static"
              color="default"
              elevation={0}
              className="filter-toolbar"
              sx={{
                minHeight: "70px",
                zIndex: 1000,
                backgroundColor: "#ffffff",
                overflowY: "auto",
              }}
            >
              <Toolbar
                sx={{
                  position: "relative",
                  zIndex: 1000,
                  pointerEvents: "auto",
                  padding: "12px 16px",
                  minHeight: "auto",
                  flexWrap: "wrap",
                  boxShadow: 3,
                  gap: 1,
                  "@media (max-width: 768px)": {
                    padding: "8px 12px",
                    gap: 0.5,
                  },

                  "@media (max-width: 480px)": {
                    padding: "6px 8px",
                    gap: 0.25,
                  },
                }}
              >
                <Grid
                  container
                  spacing={{ xs: 1, sm: 2 }}
                  alignItems="center"
                  sx={{
                    width: "100%",
                    margin: 0,
                  }}
                >
                  <MultipleSelect
                    label="Selecionar cliente(s)"
                    options={listCustomer}
                    selectedItems={selectedCustomer}
                    onSelectionChange={handleChangeCustomer}
                    onApply={applyFilters}
                    showAllByDefault={true}
                    minWidth={{ xs: "100%", sm: "300px" }}
                    width={{ xs: "100%", sm: "300px" }}
                    getOptionLabel={(option) => {
                      return option?.name || option?.label || option?.customerName || String(option);
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (!option || !value) return false;
                      return option.idcustomer === value.idcustomer;
                    }}
                  />
                  <MultipleSelect
                    label="Selecionar vendedor(es)"
                    options={listSaller}
                    selectedItems={selectedSaller}
                    onSelectionChange={handleChangeSaller}
                    onApply={applyFilters}
                    showAllByDefault={true}
                    minWidth={{ xs: "100%", sm: "300px" }}
                    width={{ xs: "100%", sm: "300px" }}
                    getOptionLabel={(option) => {
                      return option?.name || option?.label || option?.userName || String(option);
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (!option || !value) return false;
                      return option.iduser === value.iduser;
                    }}
                  />
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    lg={2}
                    sx={{
                      minWidth: { xs: "100%", sm: "300px" },
                      maxWidth: { xs: "100%", sm: "300px" },
                    }}
                  >
                    <DataRangerPicker
                      handleChange={handleDateRangeChange}
                      input={dateRange}
                      size="small"
                      width="100%"
                      minWidth={{ xs: "100%", sm: "300px" }}
                    />
                  </Grid>
                  {(selectedCustomer.length > 0 ||
                    selectedSaller.length > 0 ||
                    startDate ||
                    endDate) && (
                      <Grid
                        item
                        xs={12}
                        sm={8}
                        md={6}
                        lg={4}
                        sx={{
                          display: "flex",
                          justifyContent: { xs: "center", sm: "flex-start" },
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          onClick={clearFilters}
                          size="small"
                          className="filter-button btn-clear"
                          title="Limpar todos os filtros e mostrar todos os orçamentos"
                        >
                          <Delete style={{ fontSize: "14px" }} /> Limpar Filtros
                        </Button>
                      </Grid>
                    )}
                  <Grid item xs />

                  <Grid
                    item
                    xs={12}
                    sm={4}
                    md={2}
                    lg={1}
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "flex-end", sm: "flex-end" },
                    }}
                  >
                    <Button
                      onClick={openHandleOk}
                      className="filter-button btn-new"
                    >
                      <AddIcon style={{ fontSize: "14px" }} /> Novo
                    </Button>
                  </Grid>
                </Grid>
              </Toolbar>
            </AppBar>
            {/** Container do Kanban */}
            {columns.length > 0 ? (
              <div className="kanban-container">
                {columns.map((column) => (
                  <div
                    key={`${column.idcolumn}-${column.name}`}
                    className="kanban-column"
                  >
                    {/** Container da coluna */}
                    <div className="column-container">
                      <div className="column-header">
                        <h3>{column.name}</h3>
                        <div className="column-total-container">
                          <span className="column-total">
                            Total: R${" "}
                            {parseFloat(column.vltotalColumn || 0).toLocaleString(
                              "pt-BR",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </span>
                          <span className="column-total">
                            Quantidade: {column.tasks.length}
                          </span>
                        </div>
                      </div>
                      <div className="column-content">
                        <Column
                          idcolumn={column.idcolumn}
                          name={column.name}
                          tasks={column.tasks}
                          moveTask={moveTask}
                          vltotalColumn={column.vltotalColumn}
                          onDelete={deleteTaskIntern}
                          onCloseSalve={onReloadIntern}
                        // onArchive={archiveTaskIntern}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{ position: "relative", height: "400px", width: "100%" }}
              >
                <Grid
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "auto",
                    width: "auto",
                    padding: "20px",
                    pointerEvents: "none",
                  }}
                >
                  <span style={{ fontSize: "14px", color: "#666" }}>
                    Nenhum orçamento encontrado
                  </span>
                </Grid>
              </div>
            )}
          </Paper>
          {isMobile() && (
            <div className="zoom-container">
              <ZoomOut
                style={{ fontSize: "16px" }}
                color="primary"
                onClick={() => applyZoom("out")}
              />
              <ZoomIn
                style={{ fontSize: "16px" }}
                color="primary"
                onClick={() => applyZoom("in")}
              />
            </div>
          )}
          <Modal title={"Criar um novo orçamento"} open={isModalVisible} onOk={handleOk} onCancel={handleCancel} footer={null} centered style={{ top: "5vh" }} styles={{ body: { height: "80vh", padding: 0, margin: 0, overflow: "auto" } }} width="90vw" closable={true} maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}>
            <div><NewAvailability onCloseSave={handleSaveOk} onClose={handleSaveOk} /></div>
          </Modal>
        </Content>
      )}
    </DndProvider>
  );
};

export default MainKanban;
